import autobind from "autobind-decorator";
import { __ } from "bm-ui-kit";
import React from "react";
import type { InputHTMLAttributes, ReactNode } from "react";
import { Form, InputGroup } from "react-bootstrap";
import type { FieldRenderProps } from "react-final-form";
import { FormItem } from "./FormItem";
import type { FormItemProps, FormItemState } from "./FormItem";

const MAX_DATALIST_LENGTH = 15;
const SPLITED_DATALIST_LENGTH = 10;

export interface FormInputProps<Type = string> extends FormItemProps<Type> {
	prepend?: ReactNode;
	append?: ReactNode;

	innerRef?: React.LegacyRef<this>;
	type?: string;
	size?: "lg" | "sm";
	plaintext?: boolean;
	id?: string;
	seamless?: boolean;
	placeholder?: string;
	dataList?: Type[];
	dataListLength?: number;
	autoComplete?: string;
	maxLength?: number;
}

export type FormInputState = FormItemState;

export class FormInput<
	Type = string,
	Props extends FormInputProps<Type> = FormInputProps<Type>,
	State extends FormItemState = FormItemState,
> extends FormItem<Type, Props, State> {
	protected readonly inputElRef: React.RefObject<HTMLInputElement>;

	public constructor(props: Props) {
		super(props);

		this.inputElRef = React.createRef();
	}

	@autobind
	protected parse(value: any): Type {
		if ((this.props.type as string) === "number" && value !== "") {
			return Number(value) as unknown as Type;
		}

		return super.parse(value);
	}

	protected renderField(fieldProps: FieldRenderProps<Type>): ReactNode {
		const { id, append, prepend, size, name, dataList, disabled, seamless } = this.props;
		const {
			input: { value },
			meta: { valid, invalid, touched },
		} = fieldProps;

		let inputGroupClass = "";

		if (!disabled && touched && valid) {
			//	inputGroupClass = 'has-success';
		} else if (!disabled && touched && invalid) {
			inputGroupClass = "has-error";
		}

		if (seamless) {
			inputGroupClass += " input-group-seamless";
		}

		const itemId = this.inputElRef?.current?.id || id || name + this.id;

		const dataListId = `${itemId}-dataList`;

		return (
			<>
				<InputGroup size={size} className={inputGroupClass}>
					{prepend && (
						<InputGroup.Prepend onClick={this.handlePrependClick}>
							<InputGroup.Text>{prepend}</InputGroup.Text>
						</InputGroup.Prepend>
					)}
					{this.renderInput(fieldProps)}
					{append && (
						<InputGroup.Append>
							{typeof append === "string" ? <InputGroup.Text>{append}</InputGroup.Text> : append}
						</InputGroup.Append>
					)}
				</InputGroup>
				{this.renderDataList(dataListId, dataList, value)}
			</>
		);
	}

	protected renderInput(fieldProps: FieldRenderProps<Type>, additionalClass?: string): ReactNode {
		const { disabled, size } = this.props;
		const {
			input: { value },
			meta: { invalid, touched },
		} = fieldProps;

		return (
			<Form.Control
				{...this.getInputProps(fieldProps)}
				ref={this.inputElRef}
				className={additionalClass}
				size={size}
				value={value === undefined ? "" : (value as unknown as string)}
				isInvalid={!disabled && touched && invalid}
				onChange={(event) => {
					this.handleChange((event.currentTarget as HTMLInputElement).value as unknown as Type, fieldProps);
				}}
				onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleFocus(event, fieldProps);
				}}
				onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
					this.handleBlur(event, fieldProps);
				}}
			/>
		);
	}

	protected renderDataList(dataListId: string, dataList: Type[], value: Type): ReactNode {
		if (dataList) {
			const typed = `${value}`.toLowerCase();

			return (
				<datalist id={dataListId}>
					{dataList
						.filter((item) => {
							const listItem = `${item}`.toLowerCase();

							return (
								(!value && dataList.length < MAX_DATALIST_LENGTH) ||
								(listItem.startsWith(typed) && listItem !== typed)
							);
						})
						.slice(0, this.props.dataListLength || SPLITED_DATALIST_LENGTH)
						.map((item: Type) => (
							<option key={`${item}`} value={`${item}`} />
						))}
				</datalist>
			);
		}

		return null;
	}

	@autobind
	protected handleValidate(value: Type, allValues: Record<string, any>): string {
		const { required } = this.props;

		if (
			!this.isDisabled() &&
			required &&
			((typeof value === "number" && isNaN(value)) ||
				(typeof value === "boolean" && value === undefined) ||
				(typeof value !== "number" && typeof value !== "boolean" && !value))
		) {
			return __("Adj meg egy értéket!");
		}

		return super.handleValidate(value, allValues);
	}

	@autobind
	private handlePrependClick(): void {
		if (this.inputElRef.current) {
			this.inputElRef.current.focus();
			this.inputElRef.current.select();
		}
	}

	protected getInputProps(_fieldProps: FieldRenderProps<Type>): InputHTMLAttributes<HTMLInputElement> {
		const {
			id,
			dataList,
			disabled,
			type,
			placeholder,
			required,
			readOnly,
			maxLength,
			tabIndex,
			autoComplete,
			label,
			name,
		} = this.props;

		let dataListId: string;

		if (dataList) {
			dataListId = `${this.inputElRef?.current?.id || id || name + this.id}-dataList`;
		}

		return {
			id,
			name,
			"aria-label": !label && placeholder,
			type,
			autoComplete,
			placeholder,
			list: dataListId,
			maxLength,
			tabIndex,
			readOnly,
			disabled: disabled || readOnly,
			required,
		};
	}
}
